import RefreshIcon from '@mui/icons-material/Refresh'
import {
    Box,
    Button,
    Chip,
    IconButton,
    LinearProgress,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    Tooltip,
    Typography
} from '@mui/material'
import React from 'react'
import { Logger } from 'wdc-cube'
import { DateFns } from 'src/utils/date-fns'
import { classToFComponent, FCClassContext, LifesHubConfirmDialog } from 'src/utils/views'
import type { ExportRequestFlow, ExportRequestStatus, ExportRequestSummary } from '../../crm_integration_service'
import ProfileCard from './profile-card'
import type { CrmExportRequestsScope } from '../../gp_scopes'

const LOG = Logger.get('CrmExportRequestsSection')

const FLOW_LABEL: Record<ExportRequestFlow, string> = {
    push: 'Push (usuário)',
    pull_sync: 'Pull síncrono',
    pull_async: 'Pull assíncrono'
}

const STATUS_LABEL: Record<ExportRequestStatus, string> = {
    completed: 'Concluída',
    pending_authorization: 'Aguardando autorização',
    authorized: 'Autorizada',
    denied_by_config: 'Negada',
    failed: 'Falhou'
}

const STATUS_COLOR: Record<ExportRequestStatus, 'success' | 'warning' | 'info' | 'error' | 'default'> = {
    completed: 'success',
    pending_authorization: 'warning',
    authorized: 'info',
    denied_by_config: 'error',
    failed: 'error'
}

function origem(req: ExportRequestSummary): { label: string; color: 'primary' | 'default' } {
    return req.flow === 'push' ? { label: 'IntelMind', color: 'primary' } : { label: 'CRM externo', color: 'default' }
}

function solicitante(req: ExportRequestSummary): string {
    if (req.flow === 'push') {
        return req.requestedByName || (req.requestedBy ? `usuário #${req.requestedBy}` : '—')
    }
    return 'CRM (via API-key)'
}

function destino(req: ExportRequestSummary): string {
    if (!req.connectionName && !req.connectionCrmType) {
        return '—'
    }
    return req.connectionCrmType
        ? `${req.connectionName ?? '—'} (${req.connectionCrmType})`
        : (req.connectionName ?? '—')
}

export type CrmExportRequestsSectionProps = {
    className?: string
    scope: CrmExportRequestsScope
}

class CrmExportRequestsSectionClass implements FCClassContext<CrmExportRequestsSectionProps> {
    readonly emitReloadClick = () => {
        this.scope.onReload().catch(LOG.caught)
    }

    readonly emitAuthorizeClick = (requestId: number) => () => {
        this.scope.onAuthorize(requestId).catch(LOG.caught)
    }

    readonly emitReprocessClick = (requestId: number) => () => {
        this.scope.onReprocess(requestId).catch(LOG.caught)
    }

    readonly emitConfirmAuthorize = () => {
        this.scope.onConfirmAuthorize().catch(LOG.caught)
    }

    readonly emitCancelAuthorize = () => {
        this.scope.onCancelAuthorize().catch(LOG.caught)
    }

    readonly emitConfirmReprocess = () => {
        this.scope.onConfirmReprocess().catch(LOG.caught)
    }

    readonly emitCancelReprocess = () => {
        this.scope.onCancelReprocess().catch(LOG.caught)
    }

    scope!: CrmExportRequestsScope

    onSyncState({ scope }: CrmExportRequestsSectionProps) {
        this.scope = scope
    }

    readonly renderRequestRow = (req: ExportRequestSummary) => (
        <TableRow key={req.id}>
            <TableCell>{req.id}</TableCell>
            <TableCell>
                <Chip size="small" color={origem(req).color} label={origem(req).label} />
                <Typography
                    variant="caption"
                    sx={{
                        color: 'text.secondary',
                        display: 'block'
                    }}
                >
                    {FLOW_LABEL[req.flow]}
                </Typography>
            </TableCell>
            <TableCell>{solicitante(req)}</TableCell>
            <TableCell>{destino(req)}</TableCell>
            <TableCell align="right">{req.cnpjsCount}</TableCell>
            <TableCell>
                <Chip size="small" color={STATUS_COLOR[req.status]} label={STATUS_LABEL[req.status]} />
            </TableCell>
            <TableCell>{DateFns.formatDate(new Date(req.createdAt), 'dd/MM/yyyy HH:mm')}</TableCell>
            <TableCell align="right">
                {req.status === 'pending_authorization' && (
                    <Button size="small" variant="contained" color="warning" onClick={this.emitAuthorizeClick(req.id)}>
                        Autorizar
                    </Button>
                )}
                {req.status === 'failed' && (
                    <Button size="small" variant="outlined" color="warning" onClick={this.emitReprocessClick(req.id)}>
                        Reprocessar
                    </Button>
                )}
            </TableCell>
        </TableRow>
    )

    render({ className }: CrmExportRequestsSectionProps) {
        const scope = this.scope

        return (
            <ProfileCard caption="Integração CRM :: Solicitações de exportação" className={className}>
                <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
                    <Tooltip title="Recarregar">
                        <IconButton onClick={this.emitReloadClick}>
                            <RefreshIcon />
                        </IconButton>
                    </Tooltip>
                </Box>

                {scope.loading && <LinearProgress color="secondary" />}

                <Table size="small">
                    <TableHead>
                        <TableRow>
                            <TableCell>#</TableCell>
                            <TableCell>Origem</TableCell>
                            <TableCell>Solicitante</TableCell>
                            <TableCell>Destino (CRM)</TableCell>
                            <TableCell align="right">Qtd. CNPJs</TableCell>
                            <TableCell>Status</TableCell>
                            <TableCell>Criada em</TableCell>
                            <TableCell align="right">Ações</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {scope.requests.length === 0 && !scope.loading && (
                            <TableRow>
                                <TableCell colSpan={8}>
                                    <Typography
                                        variant="body2"
                                        sx={{
                                            color: 'text.secondary'
                                        }}
                                    >
                                        Nenhuma solicitação registrada.
                                    </Typography>
                                </TableCell>
                            </TableRow>
                        )}
                        {scope.requests.map(this.renderRequestRow)}
                    </TableBody>
                </Table>

                <LifesHubConfirmDialog
                    open={!!scope.authorizeTarget}
                    title="Autorizar solicitação"
                    subtitle={
                        scope.authorizeTarget
                            ? `#${scope.authorizeTarget.id} · ${scope.authorizeTarget.cnpjsCount ?? 0} CNPJs`
                            : undefined
                    }
                    tone="warning"
                    message="A franquia será debitada e o resultado entregue ao CRM."
                    confirmLabel="Autorizar"
                    onConfirm={this.emitConfirmAuthorize}
                    onCancel={this.emitCancelAuthorize}
                />

                <LifesHubConfirmDialog
                    open={!!scope.reprocessTarget}
                    title="Reprocessar solicitação"
                    subtitle={
                        scope.reprocessTarget
                            ? `#${scope.reprocessTarget.id} · ${scope.reprocessTarget.cnpjsCount ?? 0} CNPJs`
                            : undefined
                    }
                    tone="warning"
                    message="O job será reenfileirado e o worker tentará novamente (montagem + envio ao CRM). A franquia é debitada apenas no sucesso."
                    confirmLabel="Reprocessar"
                    onConfirm={this.emitConfirmReprocess}
                    onCancel={this.emitCancelReprocess}
                />
            </ProfileCard>
        )
    }
}

export const CrmExportRequestsSection = classToFComponent(CrmExportRequestsSectionClass, React)
export default CrmExportRequestsSection
